import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Loader2, 
  Megaphone, 
  Copy, 
  CheckCircle2,
  Calendar,
  Target,
  TrendingUp,
  X,
  Eye,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const CampaignGenerator = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generate'); // 'generate' or 'history'
  const [loading, setLoading] = useState(false);
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [activeICP, setActiveICP] = useState(null);
  
  // Form data
  const [platform, setPlatform] = useState('meta');
  const [objective, setObjective] = useState('conversion');
  const [budget, setBudget] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  
  // Generated result
  const [campaignResult, setCampaignResult] = useState(null);
  
  // History
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Detail view
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadBusinessAndICP();
    loadHistory();
  }, []);

  const loadBusinessAndICP = async () => {
    try {
      const response = await apiClient.get('/user/active-business');
      setActiveBusiness(response.data.business);
      setActiveICP(response.data.icp);
    } catch (error) {
      console.error('Error loading business:', error);
      toast.error('Error al cargar empresa activa');
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      // Load campaign packages (grouped: campaign + ads + ctas)
      const packagesRes = await apiClient.get('/campaign-packages');
      setHistory(packagesRes.data);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!activeICP) {
      toast.error('Necesitas crear un ICP primero');
      navigate('/icp');
      return;
    }

    try {
      setLoading(true);
      
      // Generate complete campaign package (Campaign + Ads + CTAs in one call)
      const packageResponse = await apiClient.post('/campaign-packages/generate', {
        platform,
        objective,
        budget: budget || null,
        additional_notes: additionalNotes || null
      });
      
      setCampaignResult({
        campaign: packageResponse.data.campaign,
        ads: packageResponse.data.ads,
        ctas: packageResponse.data.ctas
      });
      
      toast.success('¡Campaña completa generada!');
      
      // Reload history
      loadHistory();
      
    } catch (error) {
      console.error('Error generating campaign:', error);
      toast.error('Error al generar campaña');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      // Intentar usar la API moderna del Clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success('Copiado al portapapeles');
      } else {
        // Fallback para contextos no seguros o navegadores antiguos
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          toast.success('Copiado al portapapeles');
        } catch (err) {
          toast.error('No se pudo copiar. Selecciona el texto manualmente.');
        }
        
        document.body.removeChild(textArea);
      }
    } catch (err) {
      // Fallback si la API del Clipboard falla
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        toast.success('Copiado al portapapeles');
      } catch (fallbackErr) {
        toast.error('No se pudo copiar. Selecciona el texto manualmente.');
      }
      
      document.body.removeChild(textArea);
    }
  };

  // Render detail modal for selected package (contains campaign + ads + ctas)
  const renderDetailModal = () => {
    if (!selectedItem) return null;

    const { campaign, ads, ctas } = selectedItem;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 pt-28 overflow-y-auto" onClick={() => setSelectedItem(null)}>
        <div 
          className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10 rounded-t-xl">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                Paquete Completo
              </span>
              <span className="text-sm text-slate-500">
                {new Date(selectedItem.created_at).toLocaleDateString()} • {selectedItem.platform} • {selectedItem.objective}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedItem(null)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-8">
            
            {/* ========== SECTION 1: CAMPAIGN STRATEGY ========== */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-blue-200">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-blue-900">1. Estrategia de Campaña</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-blue-600 uppercase mb-1">Tipo de Campaña</h4>
                  <p className="text-slate-900 font-medium">{campaign?.campaign_type || 'N/A'}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-blue-600 uppercase mb-1">Plataforma</h4>
                  <p className="text-slate-900 font-medium">{campaign?.platform || selectedItem.platform}</p>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-blue-600 uppercase mb-1">Sub-objetivo</h4>
                <p className="text-slate-900">{campaign?.sub_objective || 'N/A'}</p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-1">Descripción</h4>
                <p className="text-slate-900 text-sm">{campaign?.description || 'N/A'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {campaign?.when_to_use && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="text-xs font-semibold text-green-700 uppercase mb-1">✅ Cuándo Usar</h4>
                    <p className="text-green-900 text-sm">{campaign.when_to_use}</p>
                  </div>
                )}
                {campaign?.when_not_to_use && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <h4 className="text-xs font-semibold text-red-700 uppercase mb-1">❌ Cuándo NO Usar</h4>
                    <p className="text-red-900 text-sm">{campaign.when_not_to_use}</p>
                  </div>
                )}
              </div>

              {campaign?.ideal_ad_format && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-1">Formato de Anuncio Ideal</h4>
                  <p className="text-slate-900 text-sm">{campaign.ideal_ad_format}</p>
                </div>
              )}

              {campaign?.justification && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="text-xs font-semibold text-blue-700 uppercase mb-1">💡 Justificación</h4>
                  <p className="text-blue-900 text-sm">{campaign.justification}</p>
                </div>
              )}
            </div>

            {/* ========== SECTION 2: ADS ========== */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-green-200">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Megaphone className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-green-900">2. Contenido de Anuncios</h3>
              </div>

              {/* Headlines */}
              {ads?.headlines?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    Headlines ({ads.headlines.length})
                    <Button size="sm" variant="ghost" onClick={() => handleCopy(ads.headlines.join('\n'))}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {ads.headlines.map((headline, idx) => (
                      <div key={idx} className="bg-green-50 rounded-lg p-3 flex items-start justify-between gap-2">
                        <p className="text-sm text-green-900 flex-1">{headline}</p>
                        <Button size="sm" variant="ghost" onClick={() => handleCopy(headline)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Body Text */}
              {ads?.body_text?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    Textos de Anuncio ({ads.body_text.length})
                    <Button size="sm" variant="ghost" onClick={() => handleCopy(ads.body_text.join('\n\n---\n\n'))}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </h4>
                  <div className="space-y-3">
                    {ads.body_text.map((text, idx) => (
                      <div key={idx} className="bg-emerald-50 rounded-lg p-3 flex items-start justify-between gap-2">
                        <p className="text-sm text-emerald-900 flex-1 whitespace-pre-wrap">{text}</p>
                        <Button size="sm" variant="ghost" onClick={() => handleCopy(text)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hooks */}
              {ads?.hooks?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Hooks ({ads.hooks.length})</h4>
                  <div className="flex flex-wrap gap-2">
                    {ads.hooks.map((hook, idx) => (
                      <span 
                        key={idx} 
                        className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm cursor-pointer hover:bg-purple-200"
                        onClick={() => handleCopy(hook)}
                      >
                        {hook}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Image Suggestions */}
              {ads?.image_suggestions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Sugerencias de Imagen</h4>
                  <div className="space-y-2">
                    {ads.image_suggestions.map((suggestion, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700">
                        📷 {suggestion}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ========== SECTION 3: CTAs ========== */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-orange-200">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-orange-900">3. CTAs (Llamadas a la Acción)</h3>
                <span className="ml-auto text-sm text-slate-500">{ctas?.channel} • {ctas?.funnel_stage}</span>
              </div>

              {ctas?.ctas?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-sm font-semibold text-slate-700">
                      {ctas.ctas.length} CTAs Generados
                    </h4>
                    <Button size="sm" variant="ghost" onClick={() => handleCopy(ctas.ctas.join('\n'))}>
                      <Copy className="w-3 h-3 mr-1" />
                      Copiar Todos
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {ctas.ctas.map((cta, idx) => (
                      <div key={idx} className="bg-orange-50 rounded-lg p-3 flex items-center justify-between gap-2 hover:bg-orange-100 transition-colors cursor-pointer" onClick={() => handleCopy(cta)}>
                        <p className="text-sm text-orange-900 flex-1 font-medium">{cta}</p>
                        <Copy className="w-3 h-3 text-orange-400" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between rounded-b-xl">
            <Button variant="outline" onClick={() => handleCopy(JSON.stringify(selectedItem, null, 2))}>
              <Copy className="w-4 h-4 mr-2" />
              Copiar Todo (JSON)
            </Button>
            <Button onClick={() => setSelectedItem(null)}>
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderGenerateTab = () => (
    <div className="space-y-6">
      {/* Business Context */}
      {activeBusiness && activeICP && (
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Target className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">Contexto de tu Negocio</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-purple-700 font-medium">Empresa:</span>
                <p className="text-purple-900">{activeBusiness.business_name || activeBusiness.business_description.substring(0, 40)}</p>
              </div>
              <div>
                <span className="text-purple-700 font-medium">Target:</span>
                <p className="text-purple-900">{activeICP.demographics?.age_range}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generate Form */}
      <Card>
        <CardHeader>
          <CardTitle>Generar Campaña Completa</CardTitle>
          <CardDescription>
            MÓNICA generará: Estrategia + Anuncios + CTAs en un solo paso
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleGenerate} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="platform">Plataforma *</Label>
                <select
                  id="platform"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="meta">Meta (Facebook/Instagram)</option>
                  <option value="google">Google Ads</option>
                  <option value="tiktok">TikTok</option>
                  <option value="linkedin">LinkedIn</option>
                </select>
              </div>

              <div>
                <Label htmlFor="objective">Objetivo *</Label>
                <select
                  id="objective"
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="awareness">Awareness (Conocimiento)</option>
                  <option value="consideration">Consideration (Consideración)</option>
                  <option value="conversion">Conversion (Conversión)</option>
                  <option value="lead_generation">Lead Generation</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="budget">Presupuesto (opcional)</Label>
              <Input
                id="budget"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="Ej: $500/mes"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notas adicionales (opcional)</Label>
              <Textarea
                id="notes"
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Ej: Enfocarnos en Black Friday, promoción especial..."
                rows={3}
              />
            </div>

            <Button type="submit" className="w-full h-12" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Generando campaña completa...
                </>
              ) : (
                <>
                  <Megaphone className="mr-2 w-5 h-5" />
                  Generar Campaña Completa
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Result */}
      {campaignResult && (
        <div className="space-y-6">
          {/* Campaign Strategy */}
          <Card className="border-2 border-blue-300">
            <CardHeader className="bg-blue-50">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Estrategia de Campaña
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCopy(JSON.stringify(campaignResult.campaign, null, 2))}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Todo
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Tipo de Campaña:</h4>
                  <p className="text-slate-900">{campaignResult.campaign?.campaign_type || 'No disponible'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Plataforma:</h4>
                  <p className="text-slate-900">{campaignResult.campaign?.platform || 'No disponible'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Sub-objetivo:</h4>
                  <p className="text-slate-900">{campaignResult.campaign?.sub_objective || 'No disponible'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Descripción:</h4>
                  <p className="text-slate-900">{campaignResult.campaign?.description || 'No disponible'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Cuándo usar:</h4>
                  <p className="text-slate-900 text-sm">{campaignResult.campaign?.when_to_use || 'No disponible'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Cuándo NO usar:</h4>
                  <p className="text-slate-900 text-sm">{campaignResult.campaign?.when_not_to_use || 'No disponible'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Formato de Anuncio Ideal:</h4>
                  <p className="text-slate-900">{campaignResult.campaign?.ideal_ad_format || 'No disponible'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Justificación:</h4>
                  <p className="text-slate-900 text-sm">{campaignResult.campaign?.justification || 'No disponible'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ads - Headlines */}
          <Card className="border-2 border-green-300">
            <CardHeader className="bg-green-50">
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-green-600" />
                Headlines Generados ({campaignResult.ads?.headlines?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-2">
                {campaignResult.ads?.headlines?.map((headline, idx) => (
                  <Card key={idx} className="bg-slate-50">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-900 flex-1">{headline}</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopy(headline)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Ads - Body Text */}
          <Card className="border-2 border-emerald-300">
            <CardHeader className="bg-emerald-50">
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-emerald-600" />
                Textos de Anuncio ({campaignResult.ads?.body_text?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {campaignResult.ads?.body_text?.map((text, idx) => (
                  <Card key={idx} className="bg-slate-50">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-slate-900 flex-1 whitespace-pre-wrap">{text}</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopy(text)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Hooks */}
          {campaignResult.ads?.hooks?.length > 0 && (
            <Card className="border-2 border-purple-300">
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-600" />
                  Hooks ({campaignResult.ads?.hooks?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-2">
                  {campaignResult.ads?.hooks?.map((hook, idx) => (
                    <Card key={idx} className="bg-slate-50">
                      <CardContent className="py-2 px-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-slate-900">{hook}</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCopy(hook)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* CTAs */}
          <Card className="border-2 border-orange-300">
            <CardHeader className="bg-orange-50">
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-orange-600" />
                CTAs Recomendados ({campaignResult.ctas?.ctas?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-3">
                {campaignResult.ctas?.ctas?.map((cta, idx) => (
                  <Card key={idx} className="bg-slate-50">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-900">{cta}</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopy(cta)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-green-900 mb-2">
                ¡Campaña Completa Lista!
              </h3>
              <p className="text-green-800 mb-4">
                Estrategia, anuncios y CTAs guardados automáticamente
              </p>
              <Button onClick={() => setActiveTab('history')} variant="outline">
                Ver Historial de Campañas
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  const renderHistoryTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Historial de Campañas
          </CardTitle>
          <CardDescription>
            {history.length} paquetes completos generados. Cada paquete incluye: Estrategia + Anuncios + CTAs.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Aún no has generado campañas</p>
              <Button onClick={() => setActiveTab('generate')} className="mt-4">
                Crear tu Primera Campaña
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((pkg, idx) => (
                <Card 
                  key={idx} 
                  className="hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer group"
                  onClick={() => setSelectedItem(pkg)}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                            Paquete Completo
                          </span>
                          <span className="px-2 py-1 rounded text-xs bg-slate-100 text-slate-600">
                            {pkg.platform}
                          </span>
                          <span className="px-2 py-1 rounded text-xs bg-slate-100 text-slate-600">
                            {pkg.objective}
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(pkg.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <div>
                          <p className="font-semibold text-slate-900">{pkg.campaign?.campaign_type || 'Campaña'}</p>
                          <p className="text-sm text-slate-600 line-clamp-1">{pkg.campaign?.sub_objective || pkg.campaign?.description}</p>
                        </div>
                        
                        {/* Quick stats */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <TrendingUp className="w-3 h-3 text-blue-500" />
                            1 Estrategia
                          </span>
                          <span className="flex items-center gap-1">
                            <Megaphone className="w-3 h-3 text-green-500" />
                            {pkg.ads?.headlines?.length || 0} Headlines
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-orange-500" />
                            {pkg.ctas?.ctas?.length || 0} CTAs
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(JSON.stringify(pkg, null, 2));
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <div className="flex items-center text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Eye className="w-4 h-4 mr-1" />
                          <span className="text-xs font-medium">Ver Todo</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Detail Modal */}
      {renderDetailModal()}
      
      {/* Page Header */}
      <div className="bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.href = '/dashboard'}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Generador de Campañas</h1>
            <p className="text-sm text-slate-600">Estrategia + Anuncios + CTAs en un solo lugar</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <Button
            onClick={() => setActiveTab('generate')}
            variant={activeTab === 'generate' ? 'default' : 'outline'}
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Generar Nueva
          </Button>
          <Button
            onClick={() => setActiveTab('history')}
            variant={activeTab === 'history' ? 'default' : 'outline'}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Historial ({history.length})
          </Button>
        </div>

        {/* Content */}
        {activeTab === 'generate' && renderGenerateTab()}
        {activeTab === 'history' && renderHistoryTab()}
      </div>
    </div>
  );
};

export default CampaignGenerator;
